import { diffMarkdown } from '../diff/diffMarkdown';
import { SkillChangeApprovalOutcome } from '../escalations/skillChangeApproval';
import {
  CardBlock,
  CardResolution,
  InteractiveCard,
  InteractiveCardType,
} from './types';

export const APPLY_TRACK_SETTINGS_CHANGE_VERB = 'apply_track_settings_change';
export const DENY_TRACK_SETTINGS_CHANGE_VERB = 'deny_track_settings_change';

export type TrackSettingsChangeFact = {
  label: string;
  from: string;
  to: string;
};

export type TrackSettingsChangeDiff = {
  label: string;
  before: string;
  after: string;
};

export type BuildTrackSettingsChangeCardParams = {
  exceptionId: string;
  changes: TrackSettingsChangeFact[];
  notes: string[];
  diffs?: TrackSettingsChangeDiff[];
  display: string;
  resolution?: CardResolution;
  fallbackUrl?: string;
};

export function buildTrackSettingsChangeCard(
  params: BuildTrackSettingsChangeCardParams
): InteractiveCard {
  const {
    exceptionId,
    changes,
    notes,
    diffs = [],
    display,
    resolution,
    fallbackUrl = '',
  } = params;

  const blocks: CardBlock[] = [
    ...notes.map((note) => ({ type: 'text' as const, text: note })),
    {
      type: 'facts',
      facts: changes.map((change) => ({
        label: change.label,
        value: `${change.from} → ${change.to}`,
      })),
    },
    ...diffs.map(buildDiffBlock),
  ];

  return {
    cardType: InteractiveCardType.TRACK_SETTINGS_CHANGE_APPROVAL,
    title: 'Proposed Track settings change',
    blocks,
    actions: [
      {
        verb: APPLY_TRACK_SETTINGS_CHANGE_VERB,
        label: 'Apply',
        style: 'primary',
        data: { exceptionId, outcome: SkillChangeApprovalOutcome.APPLIED },
        fallbackUrl,
      },
      {
        verb: DENY_TRACK_SETTINGS_CHANGE_VERB,
        label: 'Deny',
        style: 'danger',
        data: { exceptionId, outcome: SkillChangeApprovalOutcome.DENIED },
        fallbackUrl,
      },
    ],
    fallbackText: display,
    resolution,
  };
}

function buildDiffBlock(diff: TrackSettingsChangeDiff): CardBlock {
  const { hunks, unifiedText } = diffMarkdown(diff.before, diff.after);
  return { type: 'diff', label: diff.label, text: unifiedText, hunks };
}
